import { OrderGuide } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { SetOrderGuideCollectionPartitionInput } from '@/types';
import { useCallback } from 'react';
import { useSetSalesPortalOrderGuideCollectionMutation } from '@/experiences/sales-portal/domain/order-guides/blocs/documents/set-order-guide-collection.generated';

interface UseSetOrderGuideCollectionMutatorOptions {
  restaurantUUID: string;
  supplierUUID: string;
}

interface UseSetOrderGuideCollectionMutatorResult {
  /** Replaces the whole collection; resolves `false` on failure. */
  commit: (orderGuide: OrderGuide) => Promise<boolean>;
}

const toPartitionInputs = (
  orderGuide: OrderGuide,
): SetOrderGuideCollectionPartitionInput[] =>
  orderGuide.partitions.map((partition) => ({
    items: partition.itemGroups
      .flatMap((group) => group.items)
      .map((item) => ({
        par_level: item.parLevel,
        variant_pack_id: item.variantPackUUID,
      })),
    partition_id: partition.partitionUUID,
    partition_name: partition.name,
  }));

/**
 * Replaces the whole collection for the editor's autosave. `no-cache` so a save
 * never refetches or rewrites the items cache — the reducer stays the source of truth, which
 * keeps a slow save from clobbering an edit the rep just made elsewhere.
 *
 * @param options.restaurantUUID - Restaurant the collection belongs to.
 * @param options.supplierUUID - Supplier the collection belongs to.
 */
export const useSetOrderGuideCollectionMutator = ({
  restaurantUUID,
  supplierUUID,
}: UseSetOrderGuideCollectionMutatorOptions): UseSetOrderGuideCollectionMutatorResult => {
  const [setCollection] = useSetSalesPortalOrderGuideCollectionMutation({
    context: { noBatch: true },
    fetchPolicy: 'no-cache',
  });

  const commit = useCallback(
    async (orderGuide: OrderGuide) => {
      try {
        await setCollection({
          variables: {
            // Default collections must stay nameless; non-default collections
            // must keep a name or the backend rejects the save.
            collectionName: orderGuide.isDefault ? null : orderGuide.name,
            collectionUUID: orderGuide.collectionUUID,
            partitions: toPartitionInputs(orderGuide),
            restaurantUUID,
            supplierUUID,
          },
        });
        return true;
      } catch {
        // The caller reverts and toasts on `false`.
        return false;
      }
    },
    [setCollection, restaurantUUID, supplierUUID],
  );

  return { commit };
};
